import { useRef, useState, useSyncExternalStore } from 'react'
import { ScanLine, Trash2, X } from 'lucide-react'
import { useAppStore } from '@core/store'
import { isFeatureEnabled } from '@core/features'
import { formatarNumero } from '@viewer/report/documentModel'
import {
  POINT_CLOUD_ACCEPT,
  densidadePreferida,
  guardarDensidade,
  pointCloudApi,
  type PointCloudEntry,
} from './pointCloudApi'
import { BYTES_POR_PONTO, DENSIDADES, type Progresso } from './nuvemEmFluxo'

/** "10 milhões", como se diz. */
function milhoes(n: number): string {
  return `${formatarNumero(Math.round(n / 1_000_000))} milhões`
}

/** A memória que uma densidade vai ocupar, depois de lida. */
function memoria(n: number): string {
  const mb = (n * BYTES_POR_PONTO) / 1_000_000
  return mb >= 1000 ? `${(mb / 1000).toFixed(1).replace('.', ',')} GB` : `${Math.round(mb)} MB`
}

/** Só PTS e XYZ se lêem aos pedaços e se podem ler com outra densidade.
 *  Os outros formatos entram inteiros. */
function temDensidade(nome: string): boolean {
  return /\.(pts|xyz)$/i.test(nome)
}
import { LONGE_METROS, textoDaDistancia } from './colocacao'
import ComparacaoComIfc from './comparar/ComparacaoComIfc'
import { criarNuvemDeExemplo } from './comparar/exemplo'

/** A percentagem, presa entre 0 e 100.
 *
 *  Presa de propósito: se o tamanho do ficheiro vier errado, a conta
 *  passa dos 100. Aconteceu no Node, que a 32 bits enrola os 16,4 GB de
 *  um PTS para 3,6 e mostrava 460%. O browser dá o tamanho certo, mas
 *  uma barra que passa do fim não é coisa que se deixe ao acaso. */
function percentagem(p: Progresso): number {
  if (!(p.bytesTotal > 0)) return 0
  return Math.max(0, Math.min(100, Math.round((p.bytesLidos / p.bytesTotal) * 100)))
}

export default function PointCloudPanel() {
  const open = useAppStore((state) => state.pointCloudPanelOpen)
  const setOpen = useAppStore((state) => state.setPointCloudPanelOpen)
  // A carga vive na api e não aqui: uma nuvem pode entrar pela caixa
  // inicial, pelo Abrir modelo ou arrastada para a janela, e o progresso
  // de um PTS de 15 GB, uns três minutos, tem de se ver na mesma.
  const carga = useSyncExternalStore(
    pointCloudApi.subscribe,
    pointCloudApi.cargaEmCurso,
    () => null,
  )
  const erroDaCarga = useSyncExternalStore(
    pointCloudApi.subscribe,
    pointCloudApi.ultimoErro,
    () => null,
  )
  const progresso = carga?.progresso ?? null
  // A densidade da próxima importação. Lembra-se da última escolhida
  // neste browser.
  const [densidade, setDensidade] = useState(() => densidadePreferida())
  const [error, setError] = useState<string | null>(null)
  const [aCriarExemplo, setACriarExemplo] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const models = useAppStore((state) => state.models)
  const activeModelId = useAppStore((state) => state.activeModelId)
  const clouds = useSyncExternalStore(pointCloudApi.subscribe, pointCloudApi.list, () => [])
  if (!open || !isFeatureEnabled('pointClouds')) return null

  return (
    <aside className="pointer-events-auto absolute bottom-3 right-3 z-30 w-[420px] max-h-[calc(100%-1.5rem)] overflow-y-auto rounded-md border border-[var(--color-border)] bg-[var(--color-panel)] text-xs shadow-2xl">
      <header className="flex items-center gap-2 border-b border-[var(--color-border)] px-3 py-2">
        <ScanLine size={15} className="text-[var(--color-accent)]" />
        <strong>Nuvens de pontos</strong>
        <button type="button" className="ml-auto p-1" onClick={() => setOpen(false)} title="Fechar"><X size={14} /></button>
      </header>
      <div className="space-y-3 p-3">
        <input
          ref={inputRef}
          type="file"
          accept={POINT_CLOUD_ACCEPT}
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) {
              setError(null)
              void pointCloudApi.abrir(file, densidade)
            }
            event.target.value = ''
          }}
        />
        <label
          className="flex items-center gap-2 text-[10px] text-[var(--color-text-muted)]"
          title="Para PTS e XYZ grandes: quantos pontos se guardam. Um levantamento de 230 milhões não se desenha inteiro num browser. Mais pontos dá mais detalhe e pede mais à placa gráfica."
        >
          <span>Densidade</span>
          <select
            value={densidade}
            onChange={(e) => {
              const v = Number(e.target.value)
              setDensidade(v)
              guardarDensidade(v)
            }}
            className="min-w-0 flex-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5 text-[11px] text-[var(--color-text)]"
          >
            {DENSIDADES.map((d) => (
              <option key={d} value={d}>
                até {milhoes(d)} de pontos ({memoria(d)})
              </option>
            ))}
          </select>
        </label>
        <button type="button" disabled={carga !== null} className="w-full rounded border border-[var(--color-border)] px-2 py-1.5 hover:bg-[var(--color-surface)] disabled:opacity-50" onClick={() => inputRef.current?.click()}>
          {carga ? `A ler ${carga.nome}…` : 'Importar nuvem de pontos'}
        </button>
        {activeModelId && (
          // Para experimentar a comparação sem levantamento: pontos nas
          // faces dos pilares e vigas do modelo, com desvios conhecidos.
          <button
            type="button"
            disabled={carga !== null || aCriarExemplo}
            title="Gera uma nuvem de pontos a partir dos pilares e vigas do modelo activo, com ruído de laser e alguns desvios postos de propósito, para experimentar a comparação."
            className="w-full rounded border border-dashed border-[var(--color-border)] px-2 py-1 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-50"
            onClick={() => {
              setError(null)
              setACriarExemplo(true)
              void criarNuvemDeExemplo(activeModelId)
                .catch((causa) => setError(causa instanceof Error ? causa.message : String(causa)))
                .finally(() => setACriarExemplo(false))
            }}
          >
            {aCriarExemplo ? 'A criar a nuvem de exemplo…' : 'Criar nuvem de exemplo a partir do modelo'}
          </button>
        )}
        {carga && progresso && (
          <div>
            <div className="h-1.5 overflow-hidden rounded bg-[var(--color-bg)]">
              <div
                className="h-full bg-[var(--color-accent)] transition-[width]"
                style={{ width: `${percentagem(progresso)}%` }}
              />
            </div>
            <div className="mt-1 flex items-center gap-2 text-[10px] text-[var(--color-text-muted)]">
              <span>
                {percentagem(progresso)}% · {formatarNumero(progresso.guardados)} pontos guardados
              </span>
              <button
                type="button"
                onClick={() => pointCloudApi.cancelar()}
                className="ml-auto rounded border border-[var(--color-border)] px-1.5 py-0.5 hover:text-[var(--color-text)]"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
        {clouds.map((cloud) => (
          <div key={cloud.id} className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] p-2">
            <div className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate font-medium">{cloud.filename}</span>
              <span className="text-[10px] text-[var(--color-text-muted)]">{formatarNumero(cloud.pointCount)} pts</span>
              <button type="button" title="Remover a nuvem" onClick={() => pointCloudApi.remove(cloud.id)}><Trash2 size={12} /></button>
            </div>
            {activeModelId && (
              <Alinhamento cloudId={cloud.id} modelId={activeModelId} />
            )}
            {cloud.resumo && cloud.pontosNoFicheiro !== null && (
              // Diz que reduziu. Mostrar 5 milhões de 230 e deixar pensar
              // que se vê o levantamento todo seria mentir sobre ele.
              <p className="mt-1 text-[10px] text-[var(--color-text-muted)]">{cloud.resumo}</p>
            )}
            {cloud.pontosNoFicheiro !== null && temDensidade(cloud.filename) && (
              <OutraDensidade cloud={cloud} ocupado={carga !== null} />
            )}
            <label className="mt-2 flex items-center gap-2">
              <span>Tamanho</span>
              <input type="range" min="0.005" max="0.3" step="0.005" defaultValue="0.035" className="min-w-0 flex-1" onChange={(event) => pointCloudApi.setPointSize(cloud.id, Number(event.target.value))} />
            </label>
            {activeModelId && <ComparacaoComIfc cloudId={cloud.id} modelId={activeModelId} />}
          </div>
        ))}
        {(error ?? erroDaCarga) && <p className="rounded border border-[var(--color-error-border)] bg-[var(--color-error-bg)] p-2 text-[var(--color-error-text)]">{error ?? erroDaCarga}</p>}
        {models.length === 0 && clouds.length > 0 && (
          // Dito como opção e não como requisito: a nuvem abre e vê-se
          // sozinha, e o IFC só faz falta para a comparar com o projecto.
          <p className="text-[var(--color-text-muted)]">Se quiseres comparar a nuvem com o projecto, abre também o IFC.</p>
        )}
      </div>
    </aside>
  )
}

/** O aviso de que a nuvem está longe do modelo, e o botão para a pôr no
 *  sítio.
 *
 *  Com a distância à vista, porque é ela que explica o que se passa: "a
 *  nuvem desapareceu" quando se carrega um IFC georreferenciado é, quase
 *  sempre, a nuvem a 400 km do edifício. E um botão em vez de alinhar
 *  sozinho, porque a nuvem pode vir de um topógrafo com coordenadas
 *  próprias, e alinhá-la sem perguntar punha-a no sítio errado. */
function Alinhamento({ cloudId, modelId }: { cloudId: string; modelId: string }) {
  // Lida no render e não guardada: a distância muda quando se alinha,
  // e o painel volta a desenhar-se porque a lista de nuvens avisa.
  const distancia = pointCloudApi.distanciaAoModelo(cloudId, modelId)
  const longe = distancia !== null && distancia > LONGE_METROS
  const alinhar = () => {
    pointCloudApi.alinharAoModelo(cloudId, modelId)
    pointCloudApi.enquadrar(cloudId)
  }
  if (longe) {
    return (
      <div className="mt-2 rounded border border-[var(--color-warn-border)] bg-[var(--color-warn-bg)] p-2 text-[10px] text-[var(--color-warn-text)]">
        <p>
          Esta nuvem está a {textoDaDistancia(distancia)} do modelo, por isso
          não se vê ao lado dele. Acontece quando o IFC traz
          georreferenciação e a nuvem não. Se as duas vieram do mesmo
          projecto, alinha-a.
        </p>
        <button
          type="button"
          onClick={alinhar}
          className="mt-1.5 rounded border border-[var(--color-warn-border)] px-2 py-0.5 font-medium hover:bg-[var(--color-bg)]"
        >
          Alinhar ao modelo
        </button>
      </div>
    )
  }
  return (
    <div className="mt-1.5 flex items-center gap-2 text-[10px] text-[var(--color-text-muted)]">
      <button
        type="button"
        onClick={alinhar}
        title="Põe a nuvem nas mesmas coordenadas do modelo activo. Serve quando as duas foram exportadas do mesmo projecto."
        className="rounded border border-[var(--color-border)] px-1.5 py-0.5 hover:text-[var(--color-text)]"
      >
        Alinhar ao modelo
      </button>
      <button
        type="button"
        onClick={() => pointCloudApi.enquadrar(cloudId)}
        className="rounded border border-[var(--color-border)] px-1.5 py-0.5 hover:text-[var(--color-text)]"
      >
        Enquadrar
      </button>
    </div>
  )
}

/** Voltar a ler a nuvem já aberta com mais, ou menos, pontos.
 *
 *  Sem escolher o ficheiro outra vez e sem perder o alinhamento: troca
 *  só a geometria. Faltava, e depois de importar não havia maneira de
 *  pedir mais detalhe. */
function OutraDensidade({
  cloud,
  ocupado,
}: {
  cloud: PointCloudEntry
  ocupado: boolean
}) {
  const outras = DENSIDADES.filter((d) => d !== cloud.alvo)
  return (
    <div className="mt-1.5 text-[10px] text-[var(--color-text-muted)]">
      <span>Voltar a ler com</span>
      <div className="mt-1 flex flex-wrap gap-1">
        {outras.map((d) => (
          <button
            key={d}
            type="button"
            disabled={ocupado}
            onClick={() => void pointCloudApi.mudarDensidade(cloud.id, d)}
            title={`Lê o ficheiro outra vez e guarda até ${milhoes(d)} de pontos. Ocupa cerca de ${memoria(d)}. O alinhamento e o tamanho dos pontos ficam.`}
            className={`rounded border px-1.5 py-0.5 disabled:opacity-40 ${
              d > cloud.alvo
                ? 'border-[var(--color-accent-border)] text-[var(--color-accent)] hover:bg-[var(--color-accent-bg)]'
                : 'border-[var(--color-border)] hover:text-[var(--color-text)]'
            }`}
          >
            {milhoes(d)}
          </button>
        ))}
      </div>
    </div>
  )
}
